import { useState, useMemo, useRef } from 'react';
import { useForm, useFieldArray, UseFormReturn, FieldArrayWithId } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { productsApi, Product, ProductCreateData, ProductUpdateData } from '../../../api/products';
import { fetchAppSettings } from '../../../api/admin';
import { ProductFormValues } from '../types';
import { defaultFormValues, PRODUCT_TAGS } from '../constants';

export function useProductManagement() {
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [editingProduct, setEditingProduct] = useState<Product | null>(null);
    const [warehouseFilter, setWarehouseFilter] = useState<'HYB' | 'PP' | undefined>(undefined);
    const [page, setPage] = useState(1);
    const perPage = 20;

    // Lightbox state
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxImages, setLightboxImages] = useState<string[]>([]);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    // Track deleted variant IDs
    const [deletedVariantIds, setDeletedVariantIds] = useState<string[]>([]);

    // Delete confirmation
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [productToDelete, setProductToDelete] = useState<Product | null>(null);

    const settingsQuery = useQuery({
        queryKey: ['appSettings'],
        queryFn: fetchAppSettings,
    });

    const dynamicTags = useMemo(() => {
        const staticTags = [...PRODUCT_TAGS];
        const pageSettings = (settingsQuery.data as any)?.settings?.pages;
        const sponsorBrands = pageSettings?.homePage?.sponsorBrands as any[];

        if (Array.isArray(sponsorBrands)) {
            const brandTags = sponsorBrands
                .map(b => b.title?.trim())
                .filter(title => title && title.length > 0);
            return Array.from(new Set([...staticTags, ...brandTags]));
        }
        return staticTags;
    }, [settingsQuery.data]);

    const productsQuery = useQuery({
        queryKey: ['products', searchTerm, page, warehouseFilter],
        queryFn: () =>
            productsApi.getProducts({
                search: searchTerm || undefined,
                page,
                perPage: perPage,
                includeInactive: true,
                includeVariants: true,
                warehouse: warehouseFilter,
            }),
    });

    const form = useForm<ProductFormValues>({
        defaultValues: defaultFormValues,
    });

    const { fields: imageFields, append: appendImage, remove: removeImage } = useFieldArray({
        control: form.control,
        name: 'images',
    });

    const imageInputRef = useRef<HTMLInputElement | null>(null);

    const { fields: variantFields, append: appendVariant, remove: removeVariantFromArray } = useFieldArray({
        control: form.control,
        name: 'variants',
    });

    const handleRemoveVariant = (index: number) => {
        const variants = form.getValues('variants');
        const variantToRemove = variants[index];
        if (variantToRemove?.id) {
            setDeletedVariantIds(prev => [...prev, variantToRemove.id!]);
        }
        removeVariantFromArray(index);
    };

    const createProductMutation = useMutation({
        mutationFn: async (values: ProductFormValues) => {
            const payload: ProductCreateData = {
                merchantId: 'default',
                name: values.name,
                description: values.description,
                price: Number(values.price),
                salePrice: values.isOnSale ? Number(values.salePrice) : null,
                isOnSale: values.isOnSale,
                categoryId: values.categoryId && values.categoryId.trim() !== '' ? values.categoryId : undefined,
                tags: values.tags,
                soldCount: Number(values.soldCount) || 0,
                warehouse: values.warehouse,
                images: values.images.map(img => img.file),
            };
            return productsApi.createProduct(payload);
        },
        onSuccess: async (product) => {
            toast.success('สร้างสินค้าใหม่แล้ว');
            const variants = form.getValues('variants');
            if (variants.length > 0) {
                try {
                    const variantPromises = variants.map((variant, index) => {
                        const normalizedSku = variant.sku?.trim();
                        const variantData = {
                            ...variant.variantData,
                            imageIndex: (variant.imageIndex !== undefined && variant.imageIndex !== null)
                                ? variant.imageIndex
                                : index,
                        };
                        return productsApi.createVariant({
                            productId: product.id,
                            name: variant.name,
                            sku: normalizedSku ? normalizedSku : undefined,
                            priceAdjustment: Number(variant.priceAdjustment) || 0,
                            variantData: variantData,
                            sortOrder: index,
                        });
                    });
                    await Promise.allSettled(variantPromises);
                } catch (error) {
                    toast.error('เกิดข้อผิดพลาดในการสร้างตัวเลือกสินค้า');
                }
            }
            queryClient.invalidateQueries({ queryKey: ['products'] });
            setIsCreating(false);
            form.reset(defaultFormValues);
        },
        onError: (error: any) => {
            toast.error(`ไม่สามารถสร้างสินค้าได้: ${error.message}`);
        },
    });

    const updateProductMutation = useMutation({
        mutationFn: async ({ id, values }: { id: string; values: ProductFormValues }) => {
            const currentImages = form.getValues('images');
            if (currentImages.length === 0) {
                const payload: ProductUpdateData = {
                    name: values.name,
                    description: values.description,
                    price: Number(values.price),
                    salePrice: values.isOnSale ? Number(values.salePrice) : null,
                    isOnSale: values.isOnSale,
                    categoryId: values.categoryId && values.categoryId.trim() !== '' ? values.categoryId : undefined,
                    tags: values.tags,
                    soldCount: Number(values.soldCount) || 0,
                    images: [],
                };
                return productsApi.updateProduct(id, payload);
            }
            const newBase64Images = currentImages
                .filter(img => img.file.startsWith('data:'))
                .map(img => img.file);
            const payload: ProductUpdateData = {
                name: values.name,
                description: values.description,
                price: Number(values.price),
                salePrice: values.isOnSale ? Number(values.salePrice) : null,
                isOnSale: values.isOnSale,
                categoryId: values.categoryId && values.categoryId.trim() !== '' ? values.categoryId : undefined,
                tags: values.tags,
                soldCount: Number(values.soldCount) || 0,
                warehouse: values.warehouse,
                ...(newBase64Images.length > 0 ? { images: newBase64Images } : {}),
            };
            return productsApi.updateProduct(id, payload);
        },
        onSuccess: async (product) => {
            const variants = form.getValues('variants');
            const productId = editingProduct?.id || product?.id;
            try {
                if (deletedVariantIds.length > 0) {
                    await Promise.allSettled(deletedVariantIds.map(vId => productsApi.deleteVariant(vId)));
                }
                const existingVariants = variants.filter(v => v.id && v.name.trim());
                if (existingVariants.length > 0) {
                    const updatePromises = existingVariants.map((variant, index) => {
                        const variantData = {
                            ...variant.variantData,
                            imageIndex: (variant.imageIndex !== undefined && variant.imageIndex !== null)
                                ? variant.imageIndex
                                : index,
                        };
                        return productsApi.updateVariant(variant.id!, {
                            name: variant.name,
                            sku: variant.sku?.trim() || undefined,
                            priceAdjustment: Number(variant.priceAdjustment) || 0,
                            variantData: variantData,
                            sortOrder: index,
                        });
                    });
                    await Promise.allSettled(updatePromises);
                }
                const newVariants = variants.filter(v => !v.id && v.name.trim());
                if (newVariants.length > 0 && productId) {
                    const createPromises = newVariants.map((variant, index) => {
                        const variantData = {
                            ...variant.variantData,
                            imageIndex: (variant.imageIndex !== undefined && variant.imageIndex !== null)
                                ? variant.imageIndex
                                : existingVariants.length + index,
                        };
                        return productsApi.createVariant({
                            productId: productId,
                            name: variant.name,
                            sku: variant.sku?.trim() || undefined,
                            priceAdjustment: Number(variant.priceAdjustment) || 0,
                            variantData: variantData,
                            sortOrder: existingVariants.length + index,
                        });
                    });
                    await Promise.allSettled(createPromises);
                }
                toast.success('อัปเดตสินค้าเรียบร้อย');
            } catch (error: any) {
                toast.error(`อัปเดตสินค้าสำเร็จ แต่จัดการตัวเลือกไม่ได้: ${error.message}`);
            }
            queryClient.invalidateQueries({ queryKey: ['products'] });
            setEditingProduct(null);
            setDeletedVariantIds([]);
            form.reset(defaultFormValues);
            setIsCreating(false);
        },
        onError: (error: any) => {
            toast.error(`ไม่สามารถอัปเดตสินค้าได้: ${error.message}`);
        },
    });

    const deleteProductMutation = useMutation({
        mutationFn: (id: string) => productsApi.deleteProduct(id),
        onSuccess: () => {
            toast.success('ลบสินค้าเรียบร้อยแล้ว');
            queryClient.invalidateQueries({ queryKey: ['products'] });
        },
        onError: () => toast.error('ไม่สามารถลบสินค้าได้'),
    });

    const toggleProductStatusMutation = useMutation({
        mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
            return productsApi.updateProduct(id, { isActive: isActive });
        },
        onSuccess: (_, variables) => {
            toast.success(variables.isActive ? 'เปิดแสดงสินค้าแล้ว' : 'ซ่อนสินค้าแล้ว');
            queryClient.invalidateQueries({ queryKey: ['products'] });
        },
        onError: () => toast.error('ไม่สามารถเปลี่ยนสถานะสินค้าได้'),
    });

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return;
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result as string;
                appendImage({ file: base64String, preview: base64String });
            };
            reader.readAsDataURL(file);
        }
        e.target.value = '';
    };

    const handleAddImageClick = () => {
        if (imageInputRef.current) {
            imageInputRef.current.value = '';
            imageInputRef.current.click();
        }
    };

    const handleEdit = (product: Product) => {
        const resolveImg = (img: any, size: 'thumbnail' | 'medium' | 'large' = 'thumbnail') => {
            if (!img) return '';
            if (typeof img === 'string') return img;
            return (img as any)[size] ?? (img as any).large ?? (img as any).medium ?? (img as any).thumbnail ?? '';
        };
        setEditingProduct(product);
        setDeletedVariantIds([]);
        form.reset({
            name: product.name,
            description: product.description || '',
            price: product.price,
            salePrice: product.salePrice || null,
            isOnSale: product.isOnSale || false,
            categoryId: product.categoryId,
            tags: (product as any).tags || [],
            soldCount: (product as any).soldCount || 0,
            warehouse: product.warehouse || 'HYB',
            images: product.images.map(img => ({
                file: resolveImg(img, 'large'),
                preview: resolveImg(img, 'medium'),
            })),
            variants: product.variants?.map(v => ({
                id: v.id,
                name: v.name,
                sku: v.sku ?? '',
                priceAdjustment: v.priceAdjustment,
                imageIndex: v.variantData?.imageIndex,
                variantData: v.variantData,
            })) || [],
        });
        setIsCreating(true);
    };

    const handleCancelEdit = () => {
        setEditingProduct(null);
        setIsCreating(false);
        setDeletedVariantIds([]);
        form.reset(defaultFormValues);
    };

    const openLightbox = (images: Product['images'], startIndex: number) => {
        const resolveImg = (img: any, size: 'thumbnail' | 'medium' | 'large' = 'thumbnail') => {
            if (!img) return '';
            if (typeof img === 'string') return img;
            return (img as any)[size] ?? (img as any).large ?? (img as any).medium ?? (img as any).thumbnail ?? '';
        };
        const imageUrls = images.map((img: any) => resolveImg(img, 'large'));
        setLightboxImages(imageUrls);
        setLightboxIndex(startIndex);
        setLightboxOpen(true);
    };

    const handleSubmit = form.handleSubmit((values) => {
        if (editingProduct) {
            updateProductMutation.mutate({ id: editingProduct.id, values });
        } else {
            createProductMutation.mutate(values);
        }
    });

    return {
        searchTerm,
        setSearchTerm,
        isCreating,
        setIsCreating,
        editingProduct,
        warehouseFilter,
        setWarehouseFilter,
        page,
        setPage,
        lightboxOpen,
        setLightboxOpen,
        lightboxImages,
        lightboxIndex,
        deleteConfirmOpen,
        setDeleteConfirmOpen,
        productToDelete,
        setProductToDelete,
        dynamicTags,
        productsQuery,
        form,
        imageFields,
        imageInputRef,
        variantFields,
        handleRemoveVariant,
        deleteProductMutation,
        toggleProductStatusMutation,
        handleImageUpload,
        handleAddImageClick,
        handleEdit,
        handleCancelEdit,
        openLightbox,
        handleSubmit,
        perPage,
        removeImage,
        appendVariant,
    };
}
export type UseProductManagementReturn = {
    searchTerm: string;
    setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
    isCreating: boolean;
    setIsCreating: React.Dispatch<React.SetStateAction<boolean>>;
    editingProduct: Product | null;
    warehouseFilter: string;
    setWarehouseFilter: React.Dispatch<React.SetStateAction<string>>;
    page: number;
    setPage: React.Dispatch<React.SetStateAction<number>>;
    lightboxOpen: boolean;
    setLightboxOpen: React.Dispatch<React.SetStateAction<boolean>>;
    lightboxImages: string[];
    lightboxIndex: number;
    deleteConfirmOpen: boolean;
    setDeleteConfirmOpen: React.Dispatch<React.SetStateAction<boolean>>;
    productToDelete: Product | null;
    setProductToDelete: React.Dispatch<React.SetStateAction<Product | null>>;
    dynamicTags: string[];
    productsQuery: UseQueryResult<any, Error>;
    form: UseFormReturn<ProductFormValues>;
    imageFields: FieldArrayWithId<ProductFormValues, 'images', 'id'>[];
    imageInputRef: React.RefObject<HTMLInputElement | null>;
    variantFields: FieldArrayWithId<ProductFormValues, 'variants', 'id'>[];
    handleRemoveVariant: (index: number) => void;
    deleteProductMutation: UseMutationResult<void, Error, string, unknown>;
    toggleProductStatusMutation: UseMutationResult<Product, Error, {
        id: string;
        isActive: boolean;
    }, unknown>;
    handleImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleAddImageClick: () => void;
    handleEdit: (product: Product) => void;
    handleCancelEdit: () => void;
    openLightbox: (images: Product['images'], startIndex: number) => void;
    handleSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
    perPage: number;
    removeImage: (index: number) => void;
    appendVariant: (variant: any) => void;
};
